import { Link } from "react-router-dom";
import { Star, ChevronDown, Phone } from "lucide-react";

const HERO_IMG = "/images/dishes/offene-pita.webp";

function Word({ children, delay }) {
  return (
    <span
      className="word-rise inline-block mr-3"
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </span>
  );
}

export default function Hero() {
  const headlineA = "Echter Geschmack.".split(" ");
  const headlineB = "Echtes Griechenland.".split(" ");

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden text-white"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Authentisches griechisches Gericht bei Niko's Grill"
          className="ken-burns w-full h-full object-cover"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/85 via-[#0F0F0F]/55 to-[#0F0F0F]/95" />
      <div className="absolute inset-0 hero-drift opacity-90" />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative z-10 min-h-[100svh] flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 pt-32 pb-24">
            <div className="max-w-3xl">
              <p
                className="word-rise text-[11px] md:text-xs tracking-[0.32em] uppercase text-[var(--niko-gold)] mb-6 md:mb-8"
                style={{ animationDelay: "0.1s" }}
              >
                <span className="opacity-80">🫒</span> Authentisch Griechisch · Seit 44 Jahren in Höntrop
              </p>

              <h1
                data-testid="hero-headline"
                className="font-display text-[2.5rem] leading-[1.05] sm:text-6xl md:text-7xl lg:text-[88px] sm:leading-[1.02] tracking-tight"
              >
                <span className="block">
                  {headlineA.map((w, i) => (
                    <Word key={`a-${i}`} delay={0.25 + i * 0.12}>
                      {w}
                    </Word>
                  ))}
                </span>
                <span
                  className="block italic font-greek text-[var(--niko-gold)] mt-1"
                  style={{ letterSpacing: "-0.005em" }}
                >
                  {headlineB.map((w, i) => (
                    <Word key={`b-${i}`} delay={0.65 + i * 0.12}>
                      {w}
                    </Word>
                  ))}
                </span>
              </h1>

              <p
                className="word-rise mt-6 sm:mt-8 text-base md:text-lg text-white/75 max-w-xl leading-relaxed"
                style={{ animationDelay: "1.15s" }}
              >
                Mitten in Bochum-Höntrop — 943 Gäste können sich nicht irren.
              </p>

              <div
                className="word-rise mt-7 sm:mt-10 flex flex-col sm:flex-row gap-3.5 sm:gap-7"
                style={{ animationDelay: "1.4s" }}
              >
                <Link
                  to="/speisekarte"
                  data-testid="hero-cta-menu"
                  className="pulse-gold group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-sm font-semibold tracking-[0.18em] uppercase hover:bg-[var(--niko-gold-soft)] transition-colors"
                >
                  Speisekarte entdecken
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </Link>
                <a
                  href="tel:+492327995454"
                  data-testid="hero-cta-call"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full border border-white/30 text-white text-sm font-medium tracking-[0.18em] uppercase hover:bg-white/10 hover:border-white/60 transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Jetzt anrufen
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-5 sm:bottom-8 right-5 sm:right-6 lg:right-10 z-10"
          data-testid="hero-rating-badge"
        >
          <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/15 text-sm">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-4 h-4 star-fill"
                  fill="#D4A843"
                  color="#D4A843"
                  strokeWidth={1.5}
                  style={{ animationDelay: `${1.6 + i * 0.12}s` }}
                />
              ))}
            </div>
            <span className="font-semibold">4,7 / 5</span>
            <span className="text-white/65 text-xs">· Google Bewertung</span>
          </div>
        </div>

        <a
          href="#stats"
          aria-label="Nach unten scrollen"
          className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/60 hover:text-white transition-colors"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] tracking-[0.32em] uppercase">Entdecken</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent relative overflow-hidden">
            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-3 bg-[var(--niko-gold)] scroll-dot" />
          </div>
          <ChevronDown className="w-4 h-4 -mt-1 opacity-60" />
        </a>
      </div>
    </section>
  );
}
